import {Injectable} from "@angular/core";
import {BehaviorSubject, catchError, finalize, first, map, Observable, of, switchMap, tap, throwError} from "rxjs";

import {RegistrationResponse} from "../../../authentication/api/model/registration-response";
import {UserActivationApi} from "../api/user-activation.api";
import {UserActivationDto} from "../model/user-activation-dto";
import {AccountAlreadyActiveError} from "../api/error/account-already-active-error";
import {InvalidActivationLinkError} from "../api/error/invalid-activation-link-error";
import {PatientDataMissingError} from "../api/error/patient-data-missing-error";
import {ExternalUserNotFoundError} from "../api/error/external-user-not-found-error";
import {AuthenticationService} from "../../../authentication/service/authentication.service";
import {NavigationService} from "../../../navigation/service/navigation.service";
import {DuplicateEmailError} from "../../../authentication/api/error/duplicate-email-error";
import {ActivatedRoute} from "@angular/router";
import {AppointmentInformationDto} from "../model/appointment-information-dto";
import {MedicalCenterService} from "../../../medical-center/service/medical-center.service";
import {MedicalCenter} from "../../../medical-center/model/medical-center";
import {AppointmentInformation} from "../../../appointment-creation/model/appointment-information";
import {AppointmentCreationService} from "../../../appointment-creation/service/appointment-creation.service";

@Injectable({providedIn: 'root'})
export class UserActivationService {
  readonly isConfirmButtonClicked$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly userActivationApi: UserActivationApi,
    private readonly navigationService: NavigationService,
    private readonly medicalCenterService: MedicalCenterService,
    private readonly authenticationService: AuthenticationService,
    private readonly appointmentCreationService : AppointmentCreationService,
  ) {
  }

  getUserActivationInfo(userUuid: string, activationCode: string, language: string): Observable<UserActivationDto> {
    return this.userActivationApi
      .getUserActivationInfo(userUuid, activationCode, language)
      .pipe(
        catchError((err) => {
          if (
            err instanceof AccountAlreadyActiveError ||
            err instanceof ExternalUserNotFoundError ||
            err instanceof InvalidActivationLinkError ||
            err instanceof PatientDataMissingError
          ) {
            return throwError(() => err);
          }

          return throwError(() => err);
        })
      );
  }

  activateExternalUser(userUuid: string, language: string, userActivationDto: UserActivationDto): Observable<RegistrationResponse> {
    this.isConfirmButtonClicked$.next(true);

    const queryParamMap = this.activatedRoute.snapshot.queryParamMap;

    const calendarId = queryParamMap.get('calendarId');
    const motiveId = queryParamMap.get('motiveId');
    const motiveName = queryParamMap.get('motiveName');
    const categoryId = queryParamMap.get('categoryId');
    const categoryName = queryParamMap.get('categoryName');
    const medicalCenterId = queryParamMap.get('medicalCenterId');
    const startDateTime = queryParamMap.get('startDateTime');

    const shouldRedirectToAccount =
      !calendarId || !motiveId || !motiveName || !categoryId || !categoryName || !medicalCenterId || !startDateTime;

    return this.userActivationApi.activateExternalUser(userUuid, language, userActivationDto)
      .pipe(
        switchMap((res) => {
          if (shouldRedirectToAccount) {
            this.navigationService.navigateWithoutQueryParams('/account')
            return of(res);
          }

          return this.getMedicalCenterById(Number(medicalCenterId)).pipe(
            tap((medicalCenter) => {
              const selectedAppointmentInformation: AppointmentInformation = {
                categoryId: Number(categoryId),
                categoryName: categoryName,
                medicalCenter: medicalCenter,
                motiveIds: [Number(motiveId)],
                motiveName: motiveName,
                openSlot: {calendarId: Number(calendarId), time: new Date(startDateTime)}
              }

              this.appointmentCreationService.selectedAppointmentInformation$.next(selectedAppointmentInformation);
              this.appointmentCreationService.identityVerified();
            }),
            map(() => res)
          );
        }),
        catchError((err) => {
          if (
            err instanceof AccountAlreadyActiveError ||
            err instanceof ExternalUserNotFoundError ||
            err instanceof InvalidActivationLinkError ||
            err instanceof PatientDataMissingError ||
            err instanceof DuplicateEmailError
          ) {
            return throwError(() => err);
          }

          return throwError(() => err);
        }),
        tap(this.onSuccessfulAuthentication.bind(this)),
        finalize(() => {
          this.isConfirmButtonClicked$.next(false);
        }),
      );
  }

  sendActivationLinkWithAppointmentInfo(phoneNumber: string, appointmentInformationDto: AppointmentInformationDto) {
    return this.userActivationApi
      .sendActivationLinkWithAppointmentInfo(phoneNumber, appointmentInformationDto)
      .pipe(
        catchError((err) => {
          return throwError(() => err);
        })
      )
  }

  private getMedicalCenterById(medicalCenterId: number): Observable<MedicalCenter> {
    return this.medicalCenterService.getMedicalCenterById(medicalCenterId).pipe(first());
  }

  private onSuccessfulAuthentication(res: RegistrationResponse) {
    this.authenticationService.isLoggedIn$.next(true);
    this.authenticationService.isLoginDataSubmitted$.next(false);
    localStorage.setItem('externalUserEmail', res?.emailAddress || '');
    localStorage.setItem('access-token', res.token);
    if (res.patient) {
      this.authenticationService.setDisplayName(res.firstName, res.lastname);
    }
    if (!res.verified && !res.verifiedByPhone) {
      localStorage.setItem('needsPhoneVerification', 'true');
    }
    if (!res.verified && res.verifiedByPhone) {
      localStorage.setItem('needsEmailVerification', 'true');
    }
  }
}
